import { Text } from "@clipboard-health/ui-components";
import { isDefined, toError } from "@clipboard-health/util-ts";
import { Box, CircularProgress } from "@mui/material";
import { APP_V2_APP_EVENTS, useToast } from "@src/appV2/lib";
import { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { RootPaths } from "../App/paths";
import { environmentConfig } from "../environment";
import { logEvent } from "../lib/analytics";
import { compressShiftFeedbackData, decompressShiftFeedbackData } from "./useShiftFeedbackUrl";

/**
 * This component acts as a resolver for shift feedback QR codes.
 * It extracts data from the URL parameters and redirects to the shift signout URL.
 */
export function ShiftFeedbackResolver() {
  const location = useLocation();
  const history = useHistory();
  const { showErrorToast } = useToast();
  const browserOpenedRef = useRef(false);
  const [isProcessing, setIsProcessing] = useState(true);

  // Extract QR data once
  const searchParams = new URLSearchParams(location.search);
  const compressedData = searchParams.get("data");
  const scannedByWorkerId = searchParams.get("scannedBy") ?? "logged-out-worker";

  const qrData = isDefined(compressedData)
    ? decompressShiftFeedbackData(compressedData)
    : undefined;

  useEffect(() => {
    const handleError = (message: string) => {
      setIsProcessing(false);
      showErrorToast(message);
      history.push(RootPaths.HOME);
    };

    const timeoutId = setTimeout(() => {
      handleError("Request timed out. Please try again.");
    }, 15_000);

    const processQrCode = async () => {
      if (browserOpenedRef.current || !isProcessing) {
        return;
      }

      // Validate QR data
      if (!isDefined(qrData)) {
        handleError("Invalid QR code data");
        return;
      }

      try {
        browserOpenedRef.current = true;
        const enrichedQrData = compressShiftFeedbackData({
          ...qrData,
          scannedByWorkerId,
        });
        const redirectUrl = `${environmentConfig.REACT_APP_ADMIN_WEB_APP_URL}/shift-signout-feedback?data=${enrichedQrData}`;

        logEvent(APP_V2_APP_EVENTS.SHIFT_FEEDBACK_QR_CODE_OPENED_IN_APP, {
          metadata: {
            scannedByWorkerId,
            shiftId: qrData?.shiftId,
            facilityId: qrData?.facilityId,
            clockInOut: qrData?.clockInOut,
            lunchInOut: qrData?.lunchInOut,
            breakAuthorizationReason: qrData?.breakAuthorizationReason,
          },
        });

        window.location.href = redirectUrl;
        history.push(RootPaths.HOME);
      } catch (maybeError: unknown) {
        const error = toError(maybeError);
        handleError(`Error handling QR code redirect: ${error.message}`);
      }
    };

    void processQrCode();

    return () => {
      clearTimeout(timeoutId);
    };
  }, [qrData, history, showErrorToast, isProcessing, scannedByWorkerId]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      height="100vh"
      padding={2}
    >
      <CircularProgress color="primary" size={48} sx={{ marginBottom: 2 }} />
      <Text variant="body1" sx={{ marginBottom: 3 }}>
        Redirecting to signature page...
      </Text>
    </Box>
  );
}
